<template>
  <div id="Invoice-edit" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue darken-2"
      v-if="loading"
    ></v-progress-linear>
    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>
    <v-form ref="InvoiceForm" v-model="valid" v-if="!loading">
      <v-row>
        <v-col cols="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock
              :headerText="
                'Edit Invoice' + (invoice_number ? ' - ' + invoice_number : '')
              "
            ></HeaderDividerBlock>
            <v-row>
              <!-- Customer -->
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Customer Name</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <v-autocomplete
                    v-model="form.customer_id"
                    :items="customers"
                    item-text="company_name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                  ></v-autocomplete>
                </v-col>
              </v-col>

              <!-- Branch -->
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Branch Name</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <v-autocomplete
                    v-model="form.branch_id"
                    :items="branches"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                  ></v-autocomplete>
                </v-col>
              </v-col>

              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Store</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <v-autocomplete
                    v-model="form.store_id"
                    :items="stores"
                    item-text="store_name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    :rules="[rules.required]"
                  ></v-autocomplete>
                </v-col>
              </v-col>
              <!-- Invoice Date -->
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Invoice Date</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <DatePicker
                    v-model="form.invoice_date"
                    label="Select Invoice Date"
                  />
                </v-col>
              </v-col>

              <!-- Due Date -->
              <v-col cols="6" md="4" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Due Date</p>
                </v-col>
                <v-col class="pa-0 ma-0">
                  <DatePicker
                    v-model="form.due_date"
                    label="Select Due Date"
                  />
                </v-col>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="4" sm="6">
                <v-radio-group v-model="priceType" row>
                  <v-radio label="Retail" value="retail"></v-radio>
                  <v-radio label="Wholesale" value="wholesale"></v-radio>
                </v-radio-group>
              </v-col>
            </v-row>
            <!-- Items Table -->
            <v-col cols="12">
              <InvoiceItems
                :items="form.items"
                :products="products"
                :branch_id="form.branch_id"
                :store_id="form.store_id"
                :price-type="priceType"
                @invoice-items="handleInvoiceItems"
              />
            </v-col>

            <!-- Summary -->
            <v-row cols="12">
              <v-col></v-col>
              <v-col cols="12" md="4" sm="12">
                <v-col class="ma-0 pa-0">
                  <p class="text-h5 font-weight-bold">Summary</p>
                </v-col>
                <v-col class="px-7 pa-10 total-section">
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Sub Total
                    </p>
                    <p>{{ formatCurrency(calculateSubtotal()) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Total Discount
                    </p>
                    <p>{{ formatCurrency(calculateTotalDiscount()) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Total Tax
                    </p>
                    <p>{{ formatCurrency(calculateTotalTax()) }}</p>
                  </v-row>
                  <v-row class="justify-space-between">
                    <p class="text-subtitle-1 font-weight-bold text-uppercase">
                      Grand Total
                    </p>
                    <p>{{ formatCurrency(calculateGrandTotal()) }}</p>
                  </v-row>
                </v-col>
              </v-col>
            </v-row>

            <!-- Comments -->
            <v-col cols="12">
              <v-col class="pa-0 ma-0">
                <p class="text-title">Comments</p>
              </v-col>
              <v-col>
                <v-textarea
                  v-model="form.remarks"
                  outlined
                  dense
                  auto-grow
                ></v-textarea>
              </v-col>
            </v-col>

            <!-- Is Post -->
            <!-- <v-col cols="6" sm="6">
              <v-col class="pa-0 ma-0">
                <p class="text-title">Is Post</p>
              </v-col>
              <v-switch
                v-model="form.is_post"
                label="Post to GL"
                inset
              ></v-switch>
            </v-col> -->

            <!-- Actions -->
            <v-col cols="12" md="12" sm="12">
              <v-card-actions class="mt-10">
                <v-row>
                  <v-col cols="12" md="10" xs="6"></v-col>
                  <v-col cols="12" md="2" xs="6">
                    <v-btn
                      v-if="hasPermission('invoice.edit')"
                      large
                      color="#00B3FF"
                      dark
                      depressed
                      block
                      @click="submit"
                    >
                      Update Invoice
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-col>
          </v-card>
        </v-col>
      </v-row>
    </v-form>
    <Snackbars
      :text="succesMsg"
      :snackbar="snackbar"
      :color="'success'"
      @classSnackbar="clossSnackbar"
    />
  </div>
</template>

<script>
import DatePicker from "@/components/shared/DatePicker";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import Alert from "@/components/shared/Alert";
import Snackbars from "@/components/shared/Snackbars";
import { InvoiceService } from "@/services/invoice-service.js";
import { checkError } from "@/config/errors.js";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import InvoiceItems from "@/components/invoices/InvoiceItemsEdit.vue";
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");
import { StockTransferService } from "@/services/stockTransfer-service.js";

export default {
  name: "EditInvoice",

  components: {
    Alert,
    Snackbars,
    DatePicker,
    HeaderDividerBlock,
    InvoiceItems,
  },
  props: ["uuid"],
  data: () => ({
    loading: false,
    loadingData: false,
    errors: null,
    storeId: null,
    project_name: "",
    customer_name: "",
    invoice_number: "",
    snackbar: false,
    succesMsg: "",
    form: {
      job_no: null,
      invoice_date: "",
      status: "",
      project_customer: "",
      store_id: null,
      branch_id: null,
      items: [],
      is_post: false,
    },
    priceType: "retail",
    jobs: [],
    role: localStorage.getItem("role") || null,
    customers: [],
    discountActive: false,
    xeroActive: true,
    discountValue: 0,
    itemsTotal: 0,
    itemsSubTotal: 0,
    itemsTotalGST: 0,
    items: [],
    stores: [],
    branches: [],
    initialItems: [],
    dueDate: null,
    page: 1,
    search: "",
    status: ["PAID", "UNPAID"],
    rules: {
      required: (value) => !!value || "This field is required.",
    },
    valid: false,
    datePicker: {
      menu: false,
      min: new Date().toISOString().substr(0, 10),
    },
  }),

  mounted() {
    this.fetchBranchesCustomersProducts();
    this.getInvoiceInfo();
  },
  watch: {
    discountValue: function(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.calculateTotal();
      }
    },

    items: {
      handler: function(newItems, oldItems) {
        if (JSON.stringify(newItems) !== JSON.stringify(oldItems)) {
          this.calculateTotal();
          this.handleInvoiceItems(newItems);
        }
      },
      deep: true,
    },

    "form.branch_id": {
      immediate: true,
      handler(newBranchId) {
        if (newBranchId) {
          this.stores = [];

          this.fetchStores(newBranchId);
        } else {
          this.stores = [];
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      member: "profile",
      hasPermission: "hasPermission",
    }),
    ...mapSettingsGetters({
      currencyCode: "currencyCode",
      taxType: "taxType",
    }),
  },

  methods: {
    async fetchBranchesCustomersProducts() {
      this.loading = true;
      try {
        const [
          branchesResponse,
          customersResponse,
          productsResponse,
        ] = await Promise.all([
          InvoiceService.getBranches(),
          InvoiceService.getCustomers(),
          InvoiceService.getProducts(),
        ]);
        this.branches = branchesResponse.data;
        this.customers = customersResponse.data;
        this.products = productsResponse.data;
      } catch (error) {
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    async getInvoiceInfo() {
      this.loading = true;
      try {
        const response = await InvoiceService.getInvoiceDetails(this.uuid);
        const data = response.data.data;

        this.invoice_number = data.invoice_number || "";
        this.form.customer_id = data.customer?.id || "";
        this.form.branch_id = data.branch_id || "";
        this.storeId = data.store_id || "";
        this.form.invoice_date = data.invoice_date || "";
        this.form.due_date = data.invoice_due_date || "";

        this.fetchStores(this.form.branch_id);
        this.form.store_id = this.storeId || null;
        this.form.items = (data.invoice_items || []).map((item) => ({
          id: item.product_id,
          product_code: item.product_code,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.selling_price,
          discount: item.discount,
          tax_percent: item.tax_percent || 0,
          batch_id: item.batch_id || "",
          description: item.description || "",
        }));

        this.form.remarks = data.remarks || "";
        this.form.is_post = data.is_post || false;
        this.initialItems = this.form.items;
      } catch (error) {
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    formatDateForMySQL(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = ("0" + (d.getMonth() + 1)).slice(-2);
      const day = ("0" + d.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    },
    submit() {
      this.loading = true;

      this.itemsSubTotal = this.form.items.reduce(
        (sum, item) =>
          sum +
          (item.unit_price - (item.unit_price * item.discount) / 100) *
            item.quantity,
        0
      );
      this.itemsTotalGST = this.form.items.reduce(
        (sum, item) =>
          sum +
          (((item.unit_price - (item.unit_price * item.discount) / 100) *
            item.tax_percent) /
            100) *
            item.quantity,
        0
      );
      this.itemsTotal = this.itemsSubTotal + this.itemsTotalGST;

      const payload = {
        customer_id: this.form.customer_id || null,
        branch_id: this.form.branch_id || null,
        store_id: this.form.store_id || null,
        invoice_date: this.formatDateForMySQL(this.form.invoice_date),
        due_date: this.formatDateForMySQL(this.form.due_date),
        remarks: this.form.remarks || "",
        is_post: !!this.form.is_post,
        sub_total: this.itemsSubTotal || 0,
        total_gst: this.itemsTotalGST || 0,
        total_amount: this.itemsTotal || 0,
        items: this.form.items || [],
      };

      InvoiceService.updateInvoice(this.uuid, payload)
        .then((response) => {
          this.loading = false;
          if (response.status == 200) {
            this.succesMsg = response.data.message;
            this.snackbar = true;
            this.$router.push({ name: "InvoiceList" });
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response?.data || {};
          this.errors = checkError.errorFormatting(data);
        });
    },
    async fetchStores(branchId) {
      try {
        const storesResponse = await StockTransferService.getStores(branchId);
        this.stores = storesResponse.data;
        if (!this.stores.some((s) => s.id === this.form.store_id)) {
          this.form.store_id = null;
        }
      } catch (error) {
        this.stores = [];
      }
    },

    formatCurrency(value) {
      const num = parseFloat(value || 0);
      return isNaN(num) ? "0.00" : num.toFixed(2);
    },

    onDismissed() {
      this.errors = null;
    },
    clossSnackbar() {
      this.snackbar = false;
    },
    toggleDiscount() {
      this.discountActive = !this.discountActive;
    },
    toggleXero() {
      this.xeroActive = !this.xeroActive;
    },
    handleInvoiceItems(items) {
      this.form.items = items;

      this.itemsSubTotal = items.reduce((sum, item) => {
        const unitPrice = Number(item.unit_price) || 0;
        const discountPercent = Number(item.discount) || 0;
        const quantity = Number(item.quantity) || 0;
        const discountedPrice = unitPrice - (unitPrice * discountPercent) / 100;
        return sum + discountedPrice * quantity;
      }, 0);

      this.itemsTotalGST = items.reduce((sum, item) => {
        const unitPrice = Number(item.unit_price) || 0;
        const discountPercent = Number(item.discount) || 0;
        const quantity = Number(item.quantity) || 0;
        const taxPercent = Number(item.tax_percent) || 0;
        const discountedPrice = unitPrice - (unitPrice * discountPercent) / 100;
        return sum + ((discountedPrice * taxPercent) / 100) * quantity;
      }, 0);

      this.itemsTotal = this.itemsSubTotal + this.itemsTotalGST;
    },
    calculateTotal() {
      let total = this.itemsSubTotal + this.itemsTotalGST;
      if (this.discountActive && this.discountValue) {
        const discountPercentage = parseFloat(this.discountValue) || 0;
        const discountAmount = (total * discountPercentage) / 100;
        total -= discountAmount;
        this.itemsTotal = total.toFixed(2);
      }
      this.itemsTotal = total.toFixed(2);
      return total;
    },
    calculateSubtotal() {
      return this.form.items.reduce((sum, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercent = parseFloat(item.discount) || 0;
        const discountedPrice = unitPrice - (unitPrice * discountPercent) / 100;
        return sum + discountedPrice * (parseFloat(item.quantity) || 0);
      }, 0);
    },

    calculateTotalDiscount() {
      return this.form.items.reduce((totalDiscount, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercent = parseFloat(item.discount) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        const discountAmount = (unitPrice * discountPercent) / 100;
        return totalDiscount + discountAmount * quantity;
      }, 0);
    },

    calculateTotalTax() {
      return this.form.items.reduce((totalTax, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0;
        const discountPercent = parseFloat(item.discount) || 0;
        const discountedPrice = unitPrice - (unitPrice * discountPercent) / 100;
        const taxPercent = parseFloat(item.tax_percent) || 0;
        const quantity = parseFloat(item.quantity) || 0;
        return totalTax + (discountedPrice * taxPercent * quantity) / 100;
      }, 0);
    },

    calculateGrandTotal() {
      const subtotal = this.calculateSubtotal();
      const totalTax = this.calculateTotalTax();
      return Math.max(0, subtotal + totalTax);
    },

    handleDueDate(date) {
      this.dueDate = date;
    },
    reset() {
      this.discountActive = false;
      this.xeroActive = false;
      this.discountValue = 0;
      this.itemsTotal = 0;
      this.itemsSubTotal = 0;
      this.itemsTotalGST = 0;
      this.items = [];
      this.form = [];
    },
  },
};
</script>
